/**
 * Secure Storage for E2E Encryption Keys
 *
 * Stores encryption keys in browser localStorage with PBKDF2 encryption
 * Private keys are NEVER sent to the server and are encrypted at rest
 *
 * Storage Structure:
 * - e2e_keypair_v2: Encrypted keypair (PBKDF2 + NaCl)
 * - e2e_device_id: Device identifier
 * - e2e_registered: Whether public key has been registered with server
 * - e2e_user_id: User ID associated with keys
 */

import { KeyPair, SerializedKeyPair, serializeKeyPair, deserializeKeyPair } from './e2e';
import {
  storeEncryptedKeyPair,
  loadEncryptedKeyPair,
  hasEncryptedKeyPair,
  migrateToEncryptedStorage,
  clearEncryptedKeyPair,
} from './keyStorage';

const STORAGE_PREFIX = 'privacy_forum_e2e_';
const KEY_KEYPAIR_OLD = `${STORAGE_PREFIX}keypair`; // Legacy plaintext storage
const KEY_DEVICE_ID = `${STORAGE_PREFIX}device_id`;
const KEY_REGISTERED = `${STORAGE_PREFIX}registered`;
const KEY_USER_ID = `${STORAGE_PREFIX}user_id`;

// Passphrase storage (for this session only, cleared on page reload)
let sessionPassphrase: string | null = null;

export interface StoredKeyInfo {
  hasKeys: boolean;
  isRegistered: boolean;
  publicKey: string | null;
  deviceId: string | null;
  userId: string | null;
  needsMigration: boolean;
}

/**
 * Set passphrase for the current session
 * This is used to encrypt/decrypt keys without prompting every time
 */
export function setSessionPassphrase(passphrase: string): void {
  sessionPassphrase = passphrase;
}

/**
 * Get the session passphrase (if set)
 */
export function getSessionPassphrase(): string | null {
  return sessionPassphrase;
}

/**
 * Clear the session passphrase (e.g., on logout or timeout)
 */
export function clearSessionPassphrase(): void {
  sessionPassphrase = null;
}

/**
 * Check if there are legacy plaintext keys that need migration
 */
export function hasLegacyPlaintextKeys(): boolean {
  return localStorage.getItem(KEY_KEYPAIR_OLD) !== null;
}

/**
 * Store keypair in localStorage with PBKDF2 encryption
 * Requires a passphrase to encrypt the private key
 */
export async function storeKeyPair(
  keyPair: KeyPair,
  deviceId: string,
  userId: string,
  passphrase?: string
): Promise<void> {
  const passphraseToUse = passphrase || sessionPassphrase;

  if (!passphraseToUse) {
    throw new Error('Passphrase required to store encrypted keys');
  }

  // Store encrypted keypair
  await storeEncryptedKeyPair(keyPair, passphraseToUse);

  // Store metadata
  localStorage.setItem(KEY_DEVICE_ID, deviceId);
  localStorage.setItem(KEY_USER_ID, userId);
  localStorage.setItem(KEY_REGISTERED, 'false');

  // Set session passphrase if not already set
  if (!sessionPassphrase) {
    sessionPassphrase = passphraseToUse;
  }

  // Remove legacy plaintext keys if they exist
  localStorage.removeItem(KEY_KEYPAIR_OLD);
}

/**
 * Retrieve keypair from localStorage (encrypted)
 * Returns null if passphrase is not set or decryption fails
 */
export async function getKeyPair(passphrase?: string): Promise<KeyPair | null> {
  // Check for encrypted keys first
  if (hasEncryptedKeyPair()) {
    const passphraseToUse = passphrase || sessionPassphrase;

    if (!passphraseToUse) {
      return null; // Need passphrase to decrypt
    }

    try {
      const keyPair = await loadEncryptedKeyPair(passphraseToUse);

      // Set session passphrase if decryption succeeded and not already set
      if (keyPair && !sessionPassphrase) {
        sessionPassphrase = passphraseToUse;
      }

      return keyPair;
    } catch (error) {
      console.error('Failed to decrypt keypair:', error);
      return null;
    }
  }

  // Check for legacy plaintext keys
  const stored = localStorage.getItem(KEY_KEYPAIR_OLD);
  if (stored) {
    try {
      const serialized: SerializedKeyPair = JSON.parse(stored);
      return deserializeKeyPair(serialized);
    } catch (error) {
      console.error('Failed to parse stored keypair:', error);
      return null;
    }
  }

  return null;
}

/**
 * Get device ID
 */
export function getDeviceId(): string | null {
  return localStorage.getItem(KEY_DEVICE_ID);
}

/**
 * Get user ID associated with stored keys
 */
export function getUserId(): string | null {
  return localStorage.getItem(KEY_USER_ID);
}

/**
 * Mark keys as registered with server
 */
export function markAsRegistered(): void {
  localStorage.setItem(KEY_REGISTERED, 'true');
}

/**
 * Check if keys are registered with server
 */
export function isRegistered(): boolean {
  return localStorage.getItem(KEY_REGISTERED) === 'true';
}

/**
 * Get information about stored keys (synchronous check)
 * Note: publicKey may be null even if hasKeys is true (if not decrypted yet)
 */
export function getStoredKeyInfo(): StoredKeyInfo {
  const deviceId = getDeviceId();
  const userId = getUserId();
  const registered = isRegistered();
  const needsMigration = hasLegacyPlaintextKeys();
  const hasKeys = hasEncryptedKeyPair() || needsMigration;

  return {
    hasKeys,
    isRegistered: registered,
    publicKey: null, // Would need passphrase to decrypt, use getKeyPair() instead
    deviceId,
    userId,
    needsMigration,
  };
}

/**
 * Get detailed key information with decryption (async)
 * Requires passphrase if keys are encrypted
 */
export async function getDetailedKeyInfo(passphrase?: string): Promise<StoredKeyInfo & { publicKey: string | null }> {
  const keyPair = await getKeyPair(passphrase);
  const deviceId = getDeviceId();
  const userId = getUserId();
  const registered = isRegistered();
  const needsMigration = hasLegacyPlaintextKeys();

  return {
    hasKeys: keyPair !== null,
    isRegistered: registered,
    publicKey: keyPair ? serializeKeyPair(keyPair).publicKey : null,
    deviceId,
    userId,
    needsMigration,
  };
}

/**
 * Clear all stored keys (e.g., on logout or key rotation)
 */
export function clearKeys(): void {
  // Clear encrypted keys
  clearEncryptedKeyPair();

  // Clear legacy plaintext keys
  localStorage.removeItem(KEY_KEYPAIR_OLD);

  // Clear metadata
  localStorage.removeItem(KEY_DEVICE_ID);
  localStorage.removeItem(KEY_REGISTERED);
  localStorage.removeItem(KEY_USER_ID);

  // Clear session passphrase
  clearSessionPassphrase();
}

/**
 * Export keypair for backup (WARNING: Contains private key!)
 * Requires passphrase to decrypt keys first
 */
export async function exportKeysForBackup(passphrase?: string): Promise<string | null> {
  const keyPair = await getKeyPair(passphrase);
  const deviceId = getDeviceId();
  const userId = getUserId();

  if (!keyPair) {
    return null;
  }

  const backup = {
    version: 2, // Version 2: encrypted storage
    timestamp: new Date().toISOString(),
    userId,
    deviceId,
    keyPair: serializeKeyPair(keyPair),
  };

  return JSON.stringify(backup);
}

/**
 * Import keypair from backup
 * Requires passphrase to encrypt the imported keys
 */
export async function importKeysFromBackup(backupJson: string, passphrase?: string): Promise<boolean> {
  try {
    const backup = JSON.parse(backupJson);

    if (backup.version !== 1 && backup.version !== 2) {
      throw new Error('Unsupported backup version');
    }

    const keyPair = deserializeKeyPair(backup.keyPair);
    await storeKeyPair(keyPair, backup.deviceId, backup.userId, passphrase);

    return true;
  } catch (error) {
    console.error('Failed to import backup:', error);
    return false;
  }
}

/**
 * Migrate legacy plaintext keys to encrypted storage
 * Returns true if migration was successful
 */
export async function migrateLegacyKeys(passphrase: string): Promise<boolean> {
  if (!hasLegacyPlaintextKeys()) {
    return false; // No migration needed
  }

  try {
    const stored = localStorage.getItem(KEY_KEYPAIR_OLD);
    if (!stored) return false;

    const serialized: SerializedKeyPair = JSON.parse(stored);
    const keyPair = deserializeKeyPair(serialized);
    const deviceId = getDeviceId() || '';
    const userId = getUserId() || '';

    // Store with encryption
    await storeKeyPair(keyPair, deviceId, userId, passphrase);

    return true;
  } catch (error) {
    console.error('Failed to migrate legacy keys:', error);
    return false;
  }
}
